<script setup>
// ============================================================================
// BonBoZ: chọn nhân viên cho report push. Lọc CẢ TRANG (tổng, hai phễu, cần xử
// lý, theo nhân viên, theo ngày) — server làm, xem `Bonboz::PushReport`.
//
// Mặc định server chọn người đã bật thông báo (có ít nhất một máy còn đăng ký):
// người chưa từng bật push chỉ làm phồng tầng "không gửi" và kéo tụt mọi tỉ lệ.
// Họ vẫn nằm trong danh sách, có ghi chú, để tick thêm khi cần.
//
// Tick xong bấm "Áp dụng" mới tải lại: mỗi lần tải là vài câu SQL nặng, tải
// theo từng cú tick thì bấm năm người là năm lần chờ.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  // [{ id, name, registered }], từ `agent_filter.agents` của server.
  agents: { type: Array, default: () => [] },
  selected: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['apply']);

const open = ref(false);
const draft = ref(new Set());

const resetDraft = () => {
  draft.value = new Set(props.selected.map(Number));
};

watch(() => props.selected, resetDraft, { immediate: true });

const toggleOpen = () => {
  if (!open.value) resetDraft();
  open.value = !open.value;
};

const close = () => {
  open.value = false;
};

const setDraft = ids => {
  draft.value = new Set(ids);
};

const toggle = (id, checked) => {
  const next = new Set(draft.value);
  if (checked) next.add(id);
  else next.delete(id);
  draft.value = next;
};

const selectAll = () => setDraft(props.agents.map(agent => agent.id));
const selectRegistered = () =>
  setDraft(props.agents.filter(agent => agent.registered).map(a => a.id));
const clearAll = () => setDraft([]);

const apply = () => {
  // Giữ đúng thứ tự danh sách để URL / request ổn định giữa các lần bấm.
  emit(
    'apply',
    props.agents.filter(agent => draft.value.has(agent.id)).map(a => a.id)
  );
  close();
};

const changed = computed(() => {
  const current = new Set(props.selected.map(Number));
  return (
    current.size !== draft.value.size ||
    [...draft.value].some(id => !current.has(id))
  );
});

const summary = computed(() => ({
  selected: props.selected.length,
  total: props.agents.length,
}));
</script>

<template>
  <div
    v-on-click-outside="close"
    class="relative"
    data-testid="push-agent-filter"
  >
    <button
      type="button"
      class="flex items-center gap-1.5 h-8 px-3 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-2"
      :disabled="loading"
      :aria-expanded="open"
      data-testid="push-agent-filter-toggle"
      @click="toggleOpen"
    >
      <span class="i-lucide-users size-4 text-n-slate-11" aria-hidden="true" />
      <span class="text-n-slate-11">
        {{ $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.LABEL') }}
      </span>
      <span class="font-medium tabular-nums">
        {{ $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.SUMMARY', summary) }}
      </span>
      <span
        class="i-lucide-chevron-down size-4 text-n-slate-11"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="open"
      class="absolute z-20 flex flex-col mt-1 border shadow-lg w-72 max-w-[calc(100vw-2rem)] rounded-xl border-n-weak bg-n-solid-2"
      data-testid="push-agent-filter-panel"
    >
      <div
        class="flex flex-wrap gap-x-3 gap-y-1 px-3 py-2 text-xs border-b border-n-weak"
      >
        <button
          type="button"
          class="font-medium text-n-brand hover:underline"
          data-testid="push-agent-filter-all"
          @click="selectAll"
        >
          {{ $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.ALL') }}
        </button>
        <button
          type="button"
          class="font-medium text-n-brand hover:underline"
          data-testid="push-agent-filter-registered"
          @click="selectRegistered"
        >
          {{ $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.REGISTERED') }}
        </button>
        <button
          type="button"
          class="font-medium text-n-slate-11 hover:underline"
          data-testid="push-agent-filter-none"
          @click="clearAll"
        >
          {{ $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.NONE') }}
        </button>
      </div>

      <ul class="p-1 m-0 overflow-y-auto list-none max-h-72">
        <li v-for="agent in agents" :key="agent.id">
          <label
            class="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-n-alpha-2"
            :data-testid="`push-agent-filter-agent-${agent.id}`"
          >
            <Checkbox
              :model-value="draft.has(agent.id)"
              @update:model-value="checked => toggle(agent.id, checked)"
            />
            <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
              {{ agent.name }}
            </span>
            <span
              v-if="!agent.registered"
              class="text-[11px] shrink-0 text-n-amber-11"
            >
              {{ $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.NOT_REGISTERED') }}
            </span>
          </label>
        </li>
      </ul>

      <div
        class="flex items-center justify-between gap-2 px-3 py-2 border-t border-n-weak"
      >
        <span class="text-xs tabular-nums text-n-slate-11">
          {{
            $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.SUMMARY', {
              selected: draft.size,
              total: agents.length,
            })
          }}
        </span>
        <button
          type="button"
          class="h-7 px-3 text-xs font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
          :disabled="!draft.size || !changed"
          data-testid="push-agent-filter-apply"
          @click="apply"
        >
          {{ $t('BONBOZ.PUSH_REPORT.AGENT_FILTER.APPLY') }}
        </button>
      </div>
    </div>
  </div>
</template>
